import React, { FC, useState, FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Dialog } from 'components/Dialog'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { useTranslation } from 'react-i18next'
import { CustomLayer, CustomLayerUpdateParams, CustomLayerCreateParams } from 'models/custom-layer'
import { DragFileInput } from 'components/DragFileInput'
import { Alert } from 'components/Alert'
import {
  useUpdateCustomLayer,
  useFetchCustomLayer,
  useCreateCustomLayer,
} from 'services/custom-layer'
import { Flex } from 'components/Flex'
import { Spinner } from 'components/Spinner'
import { useUserContext } from 'context/user-context'

type CustomLayerDialogFields = CustomLayerCreateParams | CustomLayerUpdateParams

export const CustomLayerDialog: FC = () => {
  const { t } = useTranslation()
  const [currentUser] = useUserContext()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const { layerId } = useParams()
  const { customLayer, status: loadStatus } = useFetchCustomLayer(layerId, {
    onSuccess: (data: CustomLayer) => {
      setForm({ ...form, name: data.name })
    },
  })

  const [form, setForm] = useState<CustomLayerDialogFields>({
    id_user: +currentUser?.id!,
    name: customLayer?.name ?? '',
  })
  const [updateCustomLayer] = useUpdateCustomLayer(layerId)
  const [createCustomLayer] = useCreateCustomLayer()
  const [status, setStatus] = useState('idle')
  const mode = layerId && customLayer ? 'edit' : 'add'

  const onClose = () => {
    navigate('/datasets')
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()

    console.log(form)

    if (!form.name || (mode === 'add' && !form.file)) {
      setError(t('custom_layer_dialog.error.missing'))
      return
    }

    setStatus('loading')

    if (mode === 'edit') {
      updateCustomLayer(form, {
        onError: () => {
          setStatus('error')
          setError(t('common.messages.error'))
        },
        onSuccess: () => navigate('../edit-styles'),
      })
    }

    if (mode === 'add') {
      createCustomLayer(form as CustomLayerCreateParams, {
        onError: () => {
          setStatus('error')
          setError(t('custom_layer_dialog.error.general'))
        },
        onSuccess: (layer: CustomLayer) => navigate(`../${layer.id}/edit-styles`),
      })
    }
  }

  return (
    <Dialog aria-label={t(`custom_layer_dialog.${mode}.title`)} isOpen={true} onClose={onClose}>
      {loadStatus === 'loading' ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : (
        <Stack space="medium">
          <div>
            <Heading size="secondary">{t(`custom_layer_dialog.${mode}.title`)}</Heading>
            <Text>{t(`custom_layer_dialog.${mode}.subtitle`)}</Text>
          </div>

          <form onSubmit={onSubmit}>
            <Stack space="medium">
              <TextInput
                name="name"
                label={t('custom_layer_dialog.form.name')}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                value={form.name}
              />
              <DragFileInput
                label={t('custom_layer_dialog.form.file_label')}
                name={form.name}
                description={t('custom_layer_dialog.form.file_description')}
                acceptedFiles={['csv', 'kml', 'zip']}
                onChange={(file) => setForm({ ...form, file })}
                value={customLayer?.file}
                accept=".csv,.kml,.zip"
              />

              {form.file || layerId ? (
                <Button type="submit" loading={status === 'loading'} full>
                  {t(`custom_layer_dialog.${mode}.cta`)}
                </Button>
              ) : (
                <React.Fragment></React.Fragment>
              )}

              {status === 'success' && (
                <Alert tone="positive" full>
                  {t(`custom_layer_dialog.${mode}.success`)}
                </Alert>
              )}
              {(status === 'error' || error) && (
                <Alert tone="critical" full>
                  {error}
                </Alert>
              )}
            </Stack>
          </form>
        </Stack>
      )}
    </Dialog>
  )
}

CustomLayerDialog.displayName = 'CustomLayerDialog'
